import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {ReceiptDisplayComponent} from "../ui/receipt-display.component";
import {ProgressBarComponent} from "../ui/progress-bar.component";
import {BorderIconButtonComponent} from "../buttons/border-icon-button.component";
import {LightIconButtonComponent} from "../buttons/light-icon-button-component";
import {CircleButtonComponent} from "../buttons/circle-button.component";
import {ThemeService} from "../../services/theme-service";
import {ActivatedRoute, Router} from "@angular/router";
import {MenuService} from "../../services/menu.service";
import {AppStateService} from "../../services/app-state.service";
import {ViewportService} from "../../services/viewport-service";
import {AuthService} from "../../services/auth.service";
import {SnackbarService} from "../../services/snackbar.service";
import {PaymentService} from "../../services/payment.service";
import {ModalService} from "../../services/modal.service";
import {combineLatest, firstValueFrom, Subscription} from "rxjs";
import {PaymentSessionService} from "../../services/payment-session.service";
import MoneyUtils from "../../utils/Utils";
import {PaymentSession} from "../../models/payment-session";
import {ReviewState} from "../../models/Order";
import {DossierViewerComponent} from "../ui/dossier-viewer/dossier-viewer.component";
import {AuthorizedApiService} from "../../services/authorized-api.service";
import {FireService} from "../../services/fire.service";
import {StarRatingComponent} from "../ui/star-rating.component";
import {ActionButtonComponent} from "../buttons/action-button.component";
import {BottomSheetService} from "../../services/bottom-sheet.service";
import {TranslateService} from "../../services/translate.service";
import {ReviewService} from "../../services/review.service";

@Component({
  selector: 'app-settlement',
  standalone: true,
  imports: [
    CommonModule,
    ProgressBarComponent,
    BorderIconButtonComponent,
    LightIconButtonComponent,
    CircleButtonComponent,
    DossierViewerComponent,
    ReceiptDisplayComponent,
    StarRatingComponent,
    ActionButtonComponent
  ],
  templateUrl: './settlement.component.html',
  styles: []
})
export class SettlementComponent implements OnInit, OnDestroy {
  progress?: number;
  leftMessage?: string;
  stillItemsToPay = false;
  private sub1?: Subscription;
  private sub2?: Subscription;
  private sub3?: Subscription;
  private appStateSub?: Subscription;
  session: PaymentSession | null = null;
  showProgress: boolean = false;
  receiptKey: string | null = null;
  hasShownUIWhereThereAreItemsToPay= false;
  showReview = false;
  showThankYouState = false;
  private currentReviewState: ReviewState | null = null;

  constructor(private route: ActivatedRoute, private themeService: ThemeService, private router: Router,
              private appStateService: AppStateService, private paymentSessionService: PaymentSessionService,
              private api: AuthorizedApiService, private fire: FireService, private appState: AppStateService,
              private viewportService: ViewportService,
              private auth: AuthService, private menu: MenuService,
              private snackbarService: SnackbarService, private paymentService: PaymentService,
              private modalService:ModalService, private sheetService: BottomSheetService,
              private translate: TranslateService, public reviewService: ReviewService) {}

  ngOnInit(): void {
    this.sub2 = this.route.paramMap.subscribe(params => {
      this.receiptKey = params.get("receipt_key");
    });

    this.init();
  }

  async init(){
    const appState = await firstValueFrom(this.appState.observeAppState())
    console.log(`TAG:SET ${appState.order.user_cfg.table} ${appState.venueId}` )

    const cfg = appState.cfg;
    this.reviewService.loadConfig(cfg);
    this.reviewService.resetRatings();

    if (this.reviewService.enabled && this.receiptKey) {
      this.reviewService.fetchAndApplyReview(this.receiptKey).subscribe(() => {
        this.recomputeReviewVisibility();
      });
    }

    let wasReviewDetailOpen = false;
    this.sub3 = this.sheetService.observeActiveSheet().subscribe(sheet => {
      if (sheet?.sheetClass === 'review-detail') {
        wasReviewDetailOpen = true;
      } else if (wasReviewDetailOpen && !sheet) {
        wasReviewDetailOpen = false;
        if (!this.receiptKey) return;
        this.reviewService.fetchAndApplyReview(this.receiptKey).subscribe(() => {
          this.recomputeReviewVisibility();
        });
      }
    });

    this.appStateSub = this.appState.observeAppState().subscribe(state => {
      this.currentReviewState = state.order?.review_state ?? null;
      this.recomputeReviewVisibility();
    });

    this.sub1 = combineLatest([
      this.paymentSessionService.observePaymentSession(),
      this.fire.observeLivesplit(appState.order.user_cfg.table!, Number(appState.venueId))
    ]).subscribe(([session, livesplitData]) => {
      this.session = session;

      if (session == null) {
        this.leftMessage = undefined;
        this.showProgress = false;
        return;
      }

      const amount = this.calculateLeftAmount(livesplitData);
      console.log("Amount left to pay: ", amount);
      const left = MoneyUtils.formatNoCents(amount);

      if (amount === 0) {
        if (this.hasShownUIWhereThereAreItemsToPay) {
          this.leftMessage = this.translate.getString("Notan är betald");
          this.showProgress = true;
        } else {
          this.leftMessage = undefined;
          this.showProgress = false;
        }
      } else {
        this.leftMessage = this.translate.getString("Det finns %s kvar på notan").replace("%s", left);
        this.showProgress = true;
        this.hasShownUIWhereThereAreItemsToPay = true;
      }

      this.stillItemsToPay = amount > 0;

      const totalToPay = amount + (session?.paid_amount ?? 0);
      this.progress = Math.min(100, Math.max(0, 100 * (session?.paid_amount ?? 0) / totalToPay));
    });
  }

  ngOnDestroy(): void {
    this.sub1?.unsubscribe();
    this.sub2?.unsubscribe();
    this.sub3?.unsubscribe();
    this.appStateSub?.unsubscribe();
  }

  // Calculate the amount left to pay, this takes all orders in livesplit into account
  // not only what's left on the table
  private calculateLeftAmount(data: any): number {
    let leftAmount = 0;
    const groupCount = data.groups;

    // Iterate through each group
    for (let i = 0; i < groupCount; i++) {
      const group = data["group" + i];
      const rids = group.rids.split(",");

      // Iterate through each row ID in the group
      for (const rid of rids) {
        const row = data["rid" + rid];

        // Only add to left amount if item is neither paid nor reserved
        if (row.state !== "paid" && row.state !== "reserved") {
          const count = row.count ?? 1;  // Default to 1 if count not specified
          const priceSum = count * row.price;
          leftAmount += priceSum;
        }
      }
    }

    return leftAmount;
  }

  getString(key: string): string {
    return this.translate.getString(key);
  }

  navLivesplit() {
    this.api.activateLivesplit().subscribe({
      next: () => {
        console.log("Activated livesplit successfully");
        this.router.navigate([this.appStateService.venueId, "livesplit"]);
      },
      error: e => this.snackbarService.showMessageForError(e)
    });
  }

  viewReceipt(): void {
    // Implement view receipt logic
  }

  backToMenu(): void {
    this.router.navigate([this.appStateService.venueId, "menu", "food"]);
  }

  navReceipt() {
    this.router.navigate([this.appStateService.venueId, "receipt", this.receiptKey]);
  }

  openGoogleReview() {
    const url = this.reviewService.getGoogleReviewUrl();
    if (url) window.open(url, '_blank');
  }

  private recomputeReviewVisibility() {
    if (this.reviewService.reviewSubmitted) {
      this.showReview = this.reviewService.enabled;
      this.showThankYouState = false;
      return;
    }
    const gating = this.reviewService.evaluateGating(this.currentReviewState);
    this.showReview = !gating.hide;
    this.showThankYouState = gating.showThankYou;
  }

  onOverallRatingSelected(rating: number) {
    this.reviewService.overallRating = rating;
    this.reviewService.reviewSubmitted = true;
    this.recomputeReviewVisibility();

    if (this.receiptKey && !this.reviewService.saveDisabled) {
      this.reviewService.submitCurrentReview(this.receiptKey, 'self_service', rating).subscribe({
        next: resp => {
          console.log('Review submitted');
          this.currentReviewState = resp.review_state ?? null;
        },
        error: (e: any) => {
          console.warn('Failed to submit review:', e);
          if (e?.status === 403) {
            this.reviewService.saveDisabled = true;
          }
        }
      });
    }

    if (!this.reviewService.hasSubReviews && !this.reviewService.canShowGoogleReview(rating)) return;

    this.sheetService.displaySheetClass('review-detail', this.getString('Betygsätt ditt besök'), {
      overallRating: rating,
      type: 'self_service',
      receiptKey: this.receiptKey,
      ratingFood: this.reviewService.ratingFood,
      ratingService: this.reviewService.ratingService,
      ratingAmbiance: this.reviewService.ratingAmbiance,
      ratingValue: this.reviewService.ratingValue,
      subReviews: this.reviewService.subReviews
    });
  }
}
